<template>
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    :class="gridClasses"
    :data-cy="dataCy"
  >
    <button
      v-for="method in methods"
      :key="method.id"
      type="button"
      role="radio"
      :aria-checked="modelValue === method.id"
      :disabled="method.disabled"
      :data-cy="`method-tile-${method.id}`"
      :class="tileClasses(method)"
      @click="select(method)"
    >
      <span
        v-if="method.icon"
        class="text-gray-700 text-base leading-none"
        :class="{ 'text-blue-700': modelValue === method.id }"
      >
        <i
          v-if="typeof method.icon === 'string'"
          :class="method.icon"
          aria-hidden="true"
        />
        <component :is="method.icon" v-else class="h-5 w-5" />
      </span>
      <span
        class="text-xs font-medium text-gray-700"
        :class="{ 'text-blue-700 font-semibold': modelValue === method.id }"
      >
        {{ method.label }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed } from "vue";

export interface MethodTile {
  id: string;
  label: string;
  icon?: Component | string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    methods: MethodTile[];
    modelValue: string;
    ariaLabel?: string;
    dataCy?: string;
  }>(),
  {
    ariaLabel: "Payment method",
    dataCy: undefined
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", id: string): void;
}>();

const gridClasses = computed(() => {
  const count = props.methods.length;
  if (count <= 3) return "grid grid-cols-3 gap-2";
  if (count === 4) return "grid grid-cols-4 gap-2";
  if (count === 5) return "grid grid-cols-5 gap-2";
  return "grid grid-cols-3 sm:grid-cols-6 gap-2";
});

function tileClasses(method: MethodTile) {
  const base =
    "flex flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-3 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300";
  if (method.disabled) {
    return `${base} border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed`;
  }
  if (props.modelValue === method.id) {
    return `${base} border-blue-600 bg-blue-50 ring-1 ring-blue-600 cursor-pointer`;
  }
  return `${base} border-gray-200 bg-white hover:border-gray-400 cursor-pointer`;
}

function select(method: MethodTile) {
  if (method.disabled) return;
  emit("update:modelValue", method.id);
}
</script>
